<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <b-form
            ref="userform"
            name="userForm"
            v-on:submit.prevent="$_onSubmit"
            class="col-xs-8 col-md-5 pb-5"
        >
            <div class="form-group">
                <!-- Name -->
                <EmailInput
                    :text="$t('labelUsername')"
                    v-model="record.userName"
                    :name="'name'"
                    :state="validStateUserName"
                    @input="$_exists"
                    :plaintext="editMode"
                    :required="!editMode"
                    :maxlength="100"
                    autofocus
                    :pattern="emailRegex"
                    v-bind:validState.sync="errorEmailValid"
                >
                </EmailInput>
                <label v-show="errorInUse" class="errormsg">{{
                    $t('userInUse')
                }}</label>
                <label v-show="!errorEmailValid" class="errormsg">{{
                    $t('userIncorrectEmail')
                }}</label>
            </div>
            <div class="form-group">
                <!-- Superuser input -->
                <div v-if="userInfo.isSuper">
                    <!-- Organization list -->
                    <x-select
                        :id="`slcOrganization`"
                        :name="`slcOrganization`"
                        :text="$t('labelOrganization')"
                        :options="
                            $_convertToValueTextPair(
                                this.organizations,
                                'id',
                                'name'
                            )
                        "
                        :required="true"
                        :state="null"
                        :disabled="editMode == true"
                        :extendedClass="{ plaintext: editMode == true }"
                        :value="selectedOrgId"
                        @selected="onXSelectSelected"
                        :simpleMode="true"
                    >
                    </x-select>
                    <!-- IsSuper -->
                    <div class="input-group">
                        <XCheckbox
                            :name="'chkSuper'"
                            :text="$t('labelSuperUser')"
                            :value="record.isSuper === 1 ? true : false"
                            @input="$_toggleSuperUser"
                        >
                        </XCheckbox>
                    </div>
                </div>
                <div v-if="userInfo.isAdmin || userInfo.isSuper">
                    <!-- IsAdmin -->
                    <div class="input-group">
                        <XCheckbox
                            :name="'chkAdmin'"
                            :text="$t('labelAdminUser')"
                            :value="record.isAdmin === 1 ? true : false"
                            @input="$_toggleAdminUser"
                            :disabled="isAdminCheckboxDisabled"
                        >
                        </XCheckbox>
                    </div>
                </div>
                <div v-if="userInfo.isAdmin || userInfo.isSuper">
                    <!-- IsFeedbackEditor -->
                    <div class="input-group">
                        <XCheckbox
                            :name="'chkEditorFeedbackLayer'"
                            :text="$t('labelFeedbackLayerUser')"
                            :value="
                                record.isFeedbackEditor === 1 ? true : false
                            "
                            @input="$_toggleFeedbackLayerEditor"
                            :disabled="isFeedbackEditorCheckboxDisabled"
                        >
                        </XCheckbox>
                    </div>
                </div>
            </div>
            <!-- Gebruikersgroep -->
            <div class="form-group" v-if="userInfo.licenses.has_mobile_license">
                <x-select
                    :id="`slcUserGroup`"
                    :name="`slcUserGroup`"
                    :text="$t('labelUserGroup')"
                    :options="this.userGroups"
                    :required="false"
                    :state="null"
                    :value="selectedUserGroupId"
                    @selected="$_onUserGroupSelected"
                    :simpleMode="true"
                >
                </x-select>
            </div>
            <!-- Ondergrondlaag profiel -->
            <div
                class="form-group"
                v-if="
                    userInfo.licenses.has_mobile_license &&
                    !this.selectedUserGroupId
                "
            >
                <x-select
                    :id="`slcBaseLayer`"
                    :name="`slcBaseLayer`"
                    :text="$t('labelProfileBaseLayer')"
                    :options="
                        $_convertToValueTextPair(
                            this.profilesType1,
                            'id',
                            'name'
                        )
                    "
                    required
                    :state="null"
                    :value="selectedBaseLayerId"
                    @selected="$_onBaseLayerSelectSelected"
                    :simpleMode="true"
                >
                </x-select>
            </div>
            <!-- Theme and storage layer profiles -->
            <div
                class="form-group"
                v-if="
                    userInfo.licenses.has_mobile_license &&
                    !this.selectedUserGroupId
                "
            >
                <label class="control-label" for="ondergrond_profile">{{
                    $t('labelProfilesTheme')
                }}</label>
                <b-table
                    thead-class="d-none"
                    striped
                    hover
                    small
                    outlined
                    bordered
                    :tbody-tr-class="$_rowClass"
                    :items="profilesType2"
                    :fields="fields"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @row-clicked="$_toggleUserEnabled"
                >
                    <template v-slot:cell(userEnabled)="row">
                        <b-form-checkbox
                            v-bind:id="`${row.item.id}userEnabled`"
                            v-bind:name="`${row.item.id}userEnabled`"
                            v-model="row.item.userEnabled"
                        >
                        </b-form-checkbox>
                    </template>
                </b-table>
            </div>
            <!-- API profiles -->
            <div
                class="form-group"
                v-if="
                    userInfo.licenses.has_mobile_license &&
                    hasAnyApiLicense &&
                    !this.selectedUserGroupId
                "
            >
                <label class="control-label">{{
                    $t('labelProfileNameApi')
                }}</label>
                <b-table
                    thead-class="d-none"
                    striped
                    hover
                    small
                    outlined
                    bordered
                    :tbody-tr-class="$_rowClass"
                    :items="apiProfiles"
                    :fields="fields"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @row-clicked="$_toggleUserEnabled"
                >
                    <template v-slot:cell(userEnabled)="row">
                        <b-form-checkbox
                            v-bind:id="`${row.item.id}userEnabled`"
                            v-bind:name="`${row.item.id}userEnabled`"
                            v-model="row.item.userEnabled"
                        >
                        </b-form-checkbox>
                    </template>
                </b-table>
            </div>
            <!-- Save/cancel/remove buttons -->
            <SubmitCancelRemove
                :is-saving="isSaving"
                :is-deleting="isDeleting"
                :mode="editMode ? modeEditConst : modeAddConst"
                @cancel="$_onCancel"
                @remove="$_onRemove"
                :isBtnSaveDisabled="isBtnSaveDisabled"
            >
            </SubmitCancelRemove>
        </b-form>
    </div>
</template>

<script>
    import { convertToValueTextPair, nprogress } from '../../services/utilities';
    import { MODE_ADD, MODE_EDIT, PROFILE_TYPE } from '../../constants';
    import { orderBy } from 'lodash';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import EmailInput from '../../components/EmailInput.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import { mapState } from 'vuex';

    export default {
        mixins: [modalMixin],

        components: {
            EmailInput,
            SubmitCancelRemove,
            XCheckbox,
            'x-select': XSelect
        },

        props: { id: { type: Number, required: false, default: -1 } },

        data() {
            return {
                // Constants
                modeAddConst: MODE_ADD,
                modeEditConst: MODE_EDIT,

                userInfo: {},
                record: {},

                sortBy: 'name',
                sortDesc: false,

                fields: [
                    { key: 'name' },
                    {
                        key: 'userEnabled',
                        tdClass: 'user-enabled'
                    }
                ],

                organizations: [],
                profilesType1: [],
                profilesType2: [],
                apiProfiles: [],
                userGroups: [],

                selectedOrgId: null,
                selectedBaseLayerId: null,
                selectedUserGroupId: null,

                editMode: false,

                isSaving: false,
                isDeleting: false,

                errorInUse: false,
                errorEmailValid: true,
                // Regular expression for email
                emailRegex:
                    /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
            };
        },

        computed: {
            ...mapState(['hasAnyApiLicense']),
            /**
             * Disabled because:
             * 1) Superuser must be admin
             * 2) Admin must stay admin if organization does not have Mobile license
             * */
            isAdminCheckboxDisabled() {
                return (
                    this.record.isSuper === 1 ||
                    (!this.userInfo.licenses.has_mobile_license &&
                        this.record.isAdmin === 1)
                );
            },

            isFeedbackEditorCheckboxDisabled() {
                return this.record.isAdmin === 1;
            },

            /**
             * Check if user can be saved
             * @returns {boolean} indicating if user can be saved
             */
            isBtnSaveDisabled() {
                return !this.validStateUserName || this.isSaving || this.isDeleting;
            },

            validStateUserName() {
                return !this.errorInUse && this.errorEmailValid;
            }
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;

            if (this.id && this.id > 0) {
                this.editMode = true;
                this.title = this.$t('userCardDetailEditTitle');
            } else {
                this.editMode = false;
                this.title = this.$t('userCardDetailAddTitle');
            }
        },

        mounted() {
            // Retrieve organizations
            this.$_getOrganizations();

            if (this.editMode) {
                this.$_getUser();
            } else {
                // Retrieve all profiles
                if (this.userInfo.licenses.has_mobile_license) {
                    this.$_getAllProfiles();
                }

                // Default settings for new user
                this.record = {
                    id: -1,
                    isAdmin: false,
                    isSuper: false,
                    isFeedbackEditor: false
                };
            }
            this.$_getAllUserGroups();
        },

        methods: {
            $_convertToValueTextPair(inputArray, key, value) {
                return convertToValueTextPair(inputArray, key, value);
            },

            $_rowClass() {
                return 'tablerow';
            },

            $_toggleAdminUser(value) {
                this.record.isAdmin = value ? 1 : 0;
                if (this.record.isAdmin === 1) {
                    this.$_toggleFeedbackLayerEditor(true);
                }
            },

            $_toggleFeedbackLayerEditor(value) {
                this.record.isFeedbackEditor = value ? 1 : 0;
            },

            $_toggleUserEnabled(attribute) {
                attribute.userEnabled = !attribute.userEnabled;
            },

            $_toggleSuperUser(value) {
                this.record.isSuper = value ? 1 : 0;
                // Superuser is always an admin user
                if (this.record.isSuper === 1) {
                    this.$_toggleAdminUser(true);
                }
            },

            /**
             * Checks if the given user name already exists in the database
             * @return {void} - Sets error message if needed
             */
            async $_exists() {
                if (!this.editMode && this.record.userName) {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'UserExists',
                        parameters: {
                            userName: this.record.userName,
                            userId: this.record.id
                        }
                    });
                    this.errorInUse = result?.data;
                }
            },

            /**
             * Set the user organization property
             */
            $_setUserOrganization() {
                const me = this;

                this.organizations.forEach(function (e) {
                    if (e.id === me.userInfo.organizationId) {
                        me.selectedOrgId = e.id;
                    }
                });
            },

            async $_getUser() {

                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetUserDetail',
                        parameters: {
                            id: this.id
                        }
                    });


                    // Make object properties reactive for Vue
                    this.record = Object.assign({}, result?.data);
                    if (this.userInfo.licenses.has_mobile_license) {
                        this.$_getAllDetailProfiles();
                    }
                    // Assign userGroupId to local variable.
                    // A superuser can have many usergroupUser records but always contains max 1 record in the context of an organization.
                    // So if there is a usergroupUser, take the first one.
                    this.selectedUserGroupId =
                        this.record?.userGroupUsers[0]?.userGroupId;
                } catch (error) {
                    const data = {
                        message: this.$t('userNotFound'),
                        variant: 'danger',
                        title: this.$t('userErrorTitle'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                    // redirect to overview
                    this.$_gotoOverview();
                }
            },

            async $_getAllProfiles() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetAllProfiles'
                });
                this.$_copyProfiles(result?.data);
            },

            async $_getAllDetailProfiles() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetAllDetailProfiles'
                });
                const profiles = result?.data;
                // Set flag for user=>profile relations
                for (let p of profiles) {
                    if (this.record.profileUsers) {
                        for (let up of this.record.profileUsers) {
                            if (up.profileId === p.id) {
                                p.userEnabled = true;
                                if (p.profileTypeId === 1) {
                                    this.selectedBaseLayerId = p.id;
                                }
                                break;
                            }
                        }
                    }
                }
                this.$_copyProfiles(profiles);
            },

            $_getAllUserGroups() {
                // get all data from the backend
                this.$store
                    .dispatch('useApiData', { name: 'GetUserGroups' })
                    .then((result) => {
                        this.userGroups = this.$_convertToValueTextPair(
                            result.data,
                            'id',
                            'name'
                        );
                    });
            },

            $_copyProfiles(profiles) {
                // Copy profiles to this.profilesType1
                this.profilesType1 = profiles.filter(function (elem) {
                    return elem.profileTypeId === PROFILE_TYPE.BASELAYER;
                });

                // Copy profiles to this.profilesType2
                this.profilesType2 = profiles.filter(function (elem) {
                    return elem.profileTypeId === PROFILE_TYPE.THEME_FEEDBACK;
                });

                // Copy profiles of type API
                this.apiProfiles = profiles.filter(function (elem) {
                    return elem.profileTypeId === PROFILE_TYPE.API;
                });

                // Sort records (lodash)
                this.profilesType1 = orderBy(this.profilesType1, [
                    (o) => o.name.toLowerCase()
                ]);
                this.profilesType2 = orderBy(this.profilesType2, [
                    (o) => o.name.toLowerCase()
                ]);
                this.apiProfiles = orderBy(this.apiProfiles, [
                    (o) => o.name.toLowerCase()
                ]);
            },

            async $_getOrganizations() {
                nprogress.start();
                // Get existing data from the backend
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetOrganizationList'
                });
                // Sort records (lodash)
                this.organizations = orderBy(result?.data, [
                    (o) => o.name.toLowerCase()
                ]);
                this.$_setUserOrganization();
                nprogress.done();
            },

            /**
             * Check if this user is the current logged in user
             * @return {boolean} true if this user is the current logged in user
             */
            $_isCurrentUser: function () {
                const selectedUser = this.record.id;
                const loggedInUser = this.userInfo.id;
                return selectedUser === loggedInUser;
            },

            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/users' });
            },

            $_onBaseLayerSelectSelected(value) {
                this.selectedBaseLayerId = value;
            },

            $_onUserGroupSelected(value) {
                this.selectedUserGroupId = value;
            },

            $_onSubmit() {
                if (this.$refs.userform.checkValidity()) {
                    // Show error when saving a non-admin user for an organization with only a Web license
                    if (
                        !this.userInfo.licenses.has_mobile_license &&
                        this.userInfo.licenses.has_web_license &&
                        !this.record.isAdmin
                    ) {
                        // toaster show error
                        const data = {
                            message: this.$t('userWebLicenseOnlyAdmin'),
                            variant: 'danger',
                            autoHideDelay: 5000,
                            noCloseButton: true
                        };
                        this.showToast(data);
                    } else {
                        this.$_onSave(false);
                    }
                }
            },

            async $_onSave() {
                this.record.organizationId = this.selectedOrgId;
                this.record.isAdmin = this.record.isAdmin ? 1 : 0;
                this.record.isSuper = this.record.isSuper ? 1 : 0;
                this.record.isFeedbackEditor = this.record.isFeedbackEditor ? 1 : 0;
                this.record.orgChanged = false;

                // Copy selected items in this.profilesType2 into selectedProfiles
                let selectedProfiles = this.profilesType2.filter((p) => {
                    return p.userEnabled === true;
                });

                // Copy selected items in this.profilesType2 into selectedProfiles
                const selectedApiProfiles = this.apiProfiles.filter((p) => {
                    return p.userEnabled === true;
                });

                selectedProfiles = selectedProfiles.concat(selectedApiProfiles);
                // Append selectedProfiles with the selected ondergrond layer profile
                if (this.selectedBaseLayerId) {
                    const selectedBaseLayer = { id: this.selectedBaseLayerId };
                    selectedProfiles.push(selectedBaseLayer);
                }

                if (this.selectedUserGroupId) {
                    this.record.userGroupUsers = [
                        { userGroupId: this.selectedUserGroupId }
                    ];
                } else {
                    this.record.userGroupUsers = [];
                }

                /**
                 * Copy selectedProfiles into the profileUsers
                 */
                const me = this;
                this.record.profileUsers = selectedProfiles.map(function (p) {
                    return { id: -1, profileId: p.id, userId: me.record.id };
                });

                this.isSaving = true;

                /**
                 * Save user
                 */
                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'SaveUser',
                        data: this.record
                    });
                    const data = {
                        message: this.$t('userCardSavedConfirmation'),
                        variant: 'success',
                        autoHideDelay: 2500,
                        noCloseButton: true
                    };
                    this.$_RemoveUserFromCache();
                    this.showToast(data);
                    this.isSaving = false;

                    if (!this.editMode) {
                        this.$_gotoOverview();
                    }
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                    this.isSaving = false;
                }
            },

            async $_onRemove() {
                if (this.record.isSuper) {
                    this.msgBoxOk({ message: this.$t('userCardDeleteErrorSuper') });
                    return;
                }

                let msg = this.$t('userCardDeletionsConfirmation');
                if (this.$_isCurrentUser()) {
                    msg = this.$t('userCardDeletionsCurrentUserConfirmation');
                }

                const confirm = await this.msgBoxConfirm({ message: msg });
                if (confirm) {
                    try {
                        await this.$store.dispatch('useApiData', {
                            name: 'DeleteUser',
                            parameters: {
                                Id: this.record.id,
                                OrganizationId:
                                    this.$store.getters.userInfo.organizationId
                            }
                        });
                        const data = {
                            message: this.$t('users.removed'),
                            variant: 'success',
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.$_gotoOverview();
                    } catch (error) {
                        const data = {
                            message: this.$t(error.message),
                            variant: 'danger',
                            title: this.$t(error.title),
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.isDeleting = false;
                    } finally {
                        this.isDeleting = false;
                    }
                }
            },

            async $_RemoveUserFromCache() {
                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'RemoveUserFromCache',
                        parameters: { Id: this.record.id }
                    });
                } catch (error) {
                    const data = {
                        message: this.$t(error.message),
                        variant: 'danger',
                        title: this.$t(error.title),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            $_onCancel: function () {
                this.$_gotoOverview();
            },

            /**
             * x-select selected event handler
             * @param  {string} value [selected value]
             */
            onXSelectSelected(value) {
                this.selectedOrgId = value;
            }
        }
    };
</script>
<style>
.user-enabled {
    width: 70px;
}
</style>
